<template>
  <div id="app">
    <header class="site-header">
      <nav class="nav" aria-label="Main navigation">
        <a class="brand" href="#top" aria-label="WellGuard AS home">
          <img src="./assets/logo.png" alt="WellGuard AS logo" />
        </a>
        <div class="nav-links" aria-label="Section links">
          <a href="#technology">Technology</a>
          <a href="#mblt">MBLT</a>
          <a href="#workflow">Workflow</a>
          <a href="#applications">Applications</a>
          <a href="#ip">Patents &amp; IP</a>
        </div>
        <a class="button primary" href="mailto:post@wellguard.no">Talk with us</a>
      </nav>
    </header>

    <main id="top">
      <section class="hero" aria-labelledby="hero-title">
        <div class="hero-inner hero-grid">
          <div>
            <p class="eyebrow">Through-tubing barrier intelligence</p>
            <h1 id="hero-title">Through-tubing barrier diagnostics. No ML.</h1>
            <p class="lead">
              WellGuard AS develops measurement technology and deterministic, physics-informed
              analytics for evaluating cement, annular conditions and well integrity from inside
              existing production tubing.
            </p>
            <div class="hero-actions">
              <a class="button primary" href="#technology">Explore the technology</a>
              <a class="button" href="#workflow">See the workflow</a>
            </div>
            <div class="proof-strip" aria-label="Company technology highlights">
              <div class="proof">
                <strong>Through tubing</strong>
                <span>Measure barrier from inside tubing</span>
              </div>
              <div class="proof">
                <strong>Behind casing</strong>
                <span>Assess barrier, cement, creeping shale or settled baryte</span>
              </div>
              <div class="proof">
                <strong>MBLT tool</strong>
                <span>Downhole logging tool for measurement principle deployment</span>
              </div>
              <div class="proof">
                <strong>IP protected</strong>
                <span>Measurement principle protected</span>
              </div>
            </div>
          </div>

          <div
            class="hero-visual"
            role="img"
            aria-label="Illustration of a downhole tool measuring through tubing toward casing and cement"
          >
            <div class="well-diagram">
              <div class="cement-field"></div>
              <div class="casing"></div>
              <div class="tubing"></div>
              <div class="tool"></div>
              <div class="arm a1"></div>
              <div class="arm a2"></div>
              <div class="arm a3"></div>
              <div class="arm a4"></div>
              <div class="arm a5"></div>
              <div class="arm a6"></div>
              <div class="pulse p1"></div>
              <div class="pulse p2"></div>
            </div>
            <div class="visual-label one">
              <strong>Excitation</strong>
              Controlled acoustic energy injected from the tubing wall.
            </div>
            <div class="visual-label two">
              <strong>Response</strong>
              Multi-receiver measurements.
            </div>
            <div class="visual-label three">
              <strong>Interpretation</strong>
              Physics-informed indicators translate response into barrier confidence.
            </div>
          </div>
        </div>
      </section>

      <section id="technology" class="section white">
        <div class="section-inner">
          <div class="section-head">
            <p class="eyebrow">Technology focus</p>
            <h2>A key enabler for rig-less P&amp;A.</h2>
            <p>
              Rig time dominates the cost of permanent plugging and abandonment. Establishing
              barrier status through existing production tubing is what makes a rig-less approach
              possible &mdash; and with large numbers of ageing wells on the Norwegian shelf and
              worldwide now approaching decommissioning, that difference scales quickly.
            </p>
          </div>
          <div class="focus-grid">
            <div class="focus-block">
              <h3>A technology to re-establish confidence</h3>
              <p>
                Many wells carry uncertainty in their barrier records rather than a known defect.
                Measuring through the tubing makes it possible to identify new intervals that
                qualify as barriers in their own right, or to confirm that existing barriers remain
                intact &mdash; extending well life and avoiding a costly recompletion that may never
                have been necessary.
              </p>
            </div>
            <div class="focus-block">
              <h3>Qualifying legacy wells for CO<sub>2</sub> storage</h3>
              <p>
                CO<sub>2</sub> storage sites are usually old fields, already penetrated by legacy
                wells. Each well is a possible leak path, and each has to be documented before
                injection starts. Measuring through the tubing gives that evidence without
                re-entering every well with a rig.
              </p>
            </div>
          </div>
          <div class="cards">
            <article class="card">
              <span class="tag">MBLT</span>
              <h3>Through-tubing measurement tool</h3>
              <p>
                Downhole logging tool for measurement principle deployment. Transmitters and
                receivers couple to the tubing wall to probe mechanical response without removing
                production tubing.
              </p>
            </article>
            <article class="card">
              <span class="tag">Analytics</span>
              <h3>Physics-informed response</h3>
              <p>
                The measured response is interpreted through its physical behaviour and calibrated
                against known references, so every result can be traced back to the measurement itself.
              </p>
            </article>
            <article class="card">
              <span class="tag">Barrier evidence</span>
              <h3>From signal to decision</h3>
              <p>
                Measurement parameters are used to distinguish water and free-pipe response
                from cement behind the casing.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section id="workflow" class="section">
        <div class="section-inner">
          <div class="section-head">
            <p class="eyebrow">Workflow</p>
            <h2>Clear processing, controlled uncertainty, useful answers.</h2>
            <p>
              The processing chain is designed to keep the measurement principle visible all the way
              from raw response to barrier decision support.
            </p>
          </div>
          <div class="steps">
            <article class="step">
              <span class="step-number">1</span>
              <h3>Measure</h3>
              <p>Acquire controlled acoustic response from inside tubing using repeatable tool contact and receiver geometry.</p>
            </article>
            <article class="step">
              <span class="step-number">2</span>
              <h3>Normalize</h3>
              <p>Normalize the measured response so depth trends can be compared on a common basis.</p>
            </article>
            <article class="step">
              <span class="step-number">3</span>
              <h3>Extract features</h3>
              <p>Track parameters and consistency indicators versus measured depth.</p>
            </article>
            <article class="step">
              <span class="step-number">4</span>
              <h3>Decide</h3>
              <p>Present results with confidence and explicit limits for field decisions.</p>
            </article>
          </div>
        </div>
      </section>

      <section id="mblt" class="section white">
        <div class="section-inner split">
          <div>
            <p class="eyebrow">MBLT tool</p>
            <h2>MBLT measures where access is limited.</h2>
            <p class="lead">
              MBLT is presented here as the downhole measurement tool: acquisition from inside
              existing production tubing, with processing and quality control built around keeping
              the measurement principle visible from raw response to barrier evidence.
            </p>
            <a class="button dark" href="#ip">View IP position</a>
          </div>
          <ul class="feature-list">
            <li>
              <span class="check">&#10003;</span>
              <div>
                <strong>Designed around physical response</strong>
                <p>Signals are interpreted through decay, spectral and modal behaviour rather than a single black-box score.</p>
              </div>
            </li>
            <li>
              <span class="check">&#10003;</span>
              <div>
                <strong>Built for practical field use</strong>
                <p>The target product route favours the smallest defensible measurement set that still carries barrier information.</p>
              </div>
            </li>
            <li>
              <span class="check">&#10003;</span>
              <div>
                <strong>No machine learning in processing or interpretation</strong>
                <p>Results are derived directly from the measured signal response using deterministic physics.</p>
              </div>
            </li>
          </ul>
        </div>
      </section>

      <section id="ip" class="section dark">
        <div class="section-inner split">
          <div>
            <p class="eyebrow">Patents &amp; intellectual property</p>
            <h2>Innovation protected in seven jurisdictions.</h2>
            <p class="muted">
              WellGuard protects the core MBLT measurement principle and related interpretation
              concepts as part of a focused international IP strategy.
            </p>
          </div>
          <div class="ip-panel">
            <h3>Protected measurement principle</h3>
            <p class="muted">
              WellGuard has registered patent protection for the through-tubing measurement
              principle in the United States, United Kingdom, European Union, Canada, Brazil,
              Malaysia and Australia.
            </p>
            <div class="ip-grid">
              <div class="ip-stat">
                <strong>Principle</strong>
                <span class="muted">Through-tubing barrier diagnostics</span>
              </div>
              <div class="ip-stat">
                <strong>Coverage</strong>
                <span class="muted">USA, UK, EU, Canada, Brazil, Malaysia, Australia</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="applications" class="section white">
        <div class="section-inner">
          <div class="section-head">
            <p class="eyebrow">Applications</p>
            <h2>Barrier insight for late-life wells, remediation and future storage.</h2>
            <p>
              The same measurement philosophy can support several high-consequence decisions where
              operators need stronger evidence before committing to costly intervention.
            </p>
          </div>
          <div class="audiences">
            <article class="audience">
              <h3>P&amp;A planning</h3>
              <p>Screen barrier condition before selecting abandonment strategy or remedial scope.</p>
            </article>
            <article class="audience">
              <h3>Well integrity</h3>
              <p>Identify intervals where the mechanical response suggests degraded cement or channels.</p>
            </article>
            <article class="audience">
              <h3>CCS readiness</h3>
              <p>Support evidence-based evaluation of legacy wells considered near future storage assets.</p>
            </article>
            <article class="audience">
              <h3>Technology qualification</h3>
              <p>Build a documented trail from measurement physics to confidence-rated interpretation.</p>
            </article>
          </div>
        </div>
      </section>

      <section class="section">
        <div class="section-inner">
          <div class="quote-band">
            <p>
              The goal is simple: make barrier uncertainty measurable enough that engineers can act
              earlier, justify decisions better and avoid unnecessary intervention.
            </p>
            <a class="button" href="mailto:post@wellguard.no">Start a technical conversation</a>
          </div>
        </div>
      </section>

      <section class="cta">
        <div class="section-inner cta-row">
          <div>
            <h2>Built for the wells where answers are hard to reach.</h2>
            <p>
              WellGuard is building a practical through-tubing diagnostics route for barrier
              evaluation: clear measurement physics, transparent processing and confidence-rated
              outputs for engineering decisions.
            </p>
          </div>
          <a class="button primary" href="mailto:post@wellguard.no">Contact WellGuard</a>
        </div>
      </section>
    </main>

    <footer class="site-footer">
      <div class="footer-grid">
        <div>
          <img class="footer-logo" src="./assets/logo.png" alt="WellGuard AS logo" />
          <p class="note">
            WellGuard AS develops protected through-tubing measurement technology for barrier
            diagnostics, interpretation and well-integrity decision support.
          </p>
        </div>
        <div class="footer-links">
          <a href="#technology">Technology</a>
          <a href="#mblt">MBLT</a>
          <a href="#applications">Applications</a>
          <a href="#ip">Patents &amp; IP</a>
        </div>
      </div>
      <div class="footer-legal">
        <p>
          &copy; {{ currentYear }} WellGuard AS &middot; Salthellerveien 2, 5770 Tyssedal, Norway
          &middot; All rights reserved.
        </p>
        <p>
          WellGuard AS is a Norwegian enterprise registered in the Norwegian Register of Business
          Enterprises with entity number 914 833 973.
        </p>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'App',
  computed: {
    currentYear() {
      return new Date().getFullYear();
    },
  },
};
</script>

<style>
:root {
  --ink: #181619;
  --muted: #5f5d63;
  --line: #d9ddd3;
  --paper: #f7f8f3;
  --white: #ffffff;
  --green: #78a941;
  --green-dark: #436f24;
  --yellow: #f0cf25;
  --gold: #c7a53b;
  --slate: #293136;
  --soft-green: #edf4e5;
  --shadow: 0 22px 60px rgba(27, 38, 28, 0.14);
}

* {
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
}

body {
  margin: 0;
  color: var(--ink);
  background: var(--paper);
  font-family:
    Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont,
    "Segoe UI", sans-serif;
  line-height: 1.5;
}

a {
  color: inherit;
  text-decoration: none;
}

.site-header {
  position: sticky;
  top: 0;
  z-index: 20;
  background: rgba(247, 248, 243, 0.94);
  border-bottom: 1px solid rgba(41, 49, 54, 0.12);
  backdrop-filter: blur(14px);
}

.nav {
  width: min(1180px, calc(100% - 36px));
  min-height: 76px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 218px;
}

.brand img {
  width: 178px;
  max-width: 46vw;
  height: auto;
  display: block;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 24px;
  color: #353238;
  font-size: 0.95rem;
  white-space: nowrap;
}

.nav-links a {
  padding: 8px 0;
  border-bottom: 2px solid transparent;
}

.nav-links a:hover {
  border-color: var(--green);
}

.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 18px;
  border: 1px solid rgba(41, 49, 54, 0.16);
  border-radius: 6px;
  background: var(--white);
  color: var(--ink);
  font-weight: 700;
  box-shadow: 0 8px 22px rgba(41, 49, 54, 0.08);
}

.button.primary {
  color: #11150f;
  border-color: transparent;
  background: linear-gradient(135deg, var(--yellow), #9fc64b);
}

.button.dark {
  color: var(--white);
  background: var(--slate);
  border-color: var(--slate);
}

.hero {
  min-height: calc(100vh - 76px);
  padding: 80px 0 72px;
  background:
    radial-gradient(circle at 76% 22%, rgba(120, 169, 65, 0.18), transparent 34%),
    linear-gradient(135deg, #fbfcf7 0%, #edf4e6 48%, #f7f8f3 100%);
  overflow: hidden;
}

.hero-inner,
.section-inner {
  width: min(1180px, calc(100% - 36px));
  margin: 0 auto;
}

.hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.04fr) minmax(360px, 0.96fr);
  align-items: center;
  gap: 48px;
}

.eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  margin: 0 0 18px;
  color: var(--green-dark);
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-size: 0.78rem;
}

.eyebrow::before {
  content: "";
  width: 32px;
  height: 3px;
  border-radius: 999px;
  background: var(--yellow);
}

h1,
h2,
h3,
p {
  margin-top: 0;
}

h1 {
  max-width: 790px;
  margin-bottom: 22px;
  font-size: clamp(3rem, 7vw, 5.8rem);
  line-height: 0.95;
  letter-spacing: -0.04em;
  color: #17151a;
}

.lead {
  max-width: 680px;
  margin-bottom: 30px;
  color: #4a484e;
  font-size: clamp(1.08rem, 2vw, 1.32rem);
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 34px;
}

.proof-strip {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  max-width: 900px;
  border-top: 1px solid rgba(41, 49, 54, 0.14);
  border-bottom: 1px solid rgba(41, 49, 54, 0.14);
}

.proof {
  padding: 16px 18px;
  border-right: 1px solid rgba(41, 49, 54, 0.12);
}

.proof:last-child {
  border-right: 0;
}

.proof strong {
  display: block;
  font-size: 1.02rem;
}

.proof span {
  color: var(--muted);
  font-size: 0.86rem;
}

.hero-visual {
  position: relative;
  min-height: 590px;
  border-radius: 8px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.72), rgba(255, 255, 255, 0.36)),
    radial-gradient(circle at 50% 38%, #ffffff, #e7eedf 62%, #d5dfcc 100%);
  border: 1px solid rgba(41, 49, 54, 0.14);
  box-shadow: var(--shadow);
  overflow: hidden;
}

.well-diagram {
  position: absolute;
  inset: 52px 48px 48px 48px;
  display: grid;
  place-items: center;
}

.cement-field {
  position: absolute;
  inset: 12px 84px 12px 84px;
  border-radius: 999px;
  background:
    linear-gradient(90deg, rgba(120, 169, 65, 0.13), rgba(240, 207, 37, 0.16)),
    repeating-linear-gradient(
      0deg,
      rgba(67, 111, 36, 0.11) 0,
      rgba(67, 111, 36, 0.11) 7px,
      transparent 7px,
      transparent 19px
    );
  border: 1px solid rgba(67, 111, 36, 0.18);
}

.casing {
  position: absolute;
  width: 216px;
  height: 100%;
  border-radius: 120px;
  border: 18px solid #a9b1b7;
  background: rgba(255, 255, 255, 0.58);
  box-shadow: inset 0 0 0 2px rgba(41, 49, 54, 0.14);
}

.tubing {
  position: absolute;
  width: 86px;
  height: 100%;
  border-radius: 80px;
  border: 13px solid #4b5358;
  background: linear-gradient(90deg, #c7e2ec, #f8ffff, #c7e2ec);
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.7),
    0 0 0 1px rgba(41, 49, 54, 0.2);
}

.tool {
  position: absolute;
  width: 42px;
  height: 66%;
  border-radius: 30px;
  background: linear-gradient(180deg, #252b2f, #5b646a);
  box-shadow: 0 16px 36px rgba(41, 49, 54, 0.24);
}

.arm {
  position: absolute;
  left: 50%;
  width: 124px;
  height: 3px;
  background: #2f363a;
  transform-origin: 0 50%;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.26);
}

.arm::after {
  content: "";
  position: absolute;
  right: -7px;
  top: -7px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--yellow);
  border: 2px solid #252b2f;
}

.arm.a1 { top: 32%; transform: rotate(0deg); }
.arm.a2 { top: 32%; transform: rotate(180deg); }
.arm.a3 { top: 48%; transform: rotate(50deg); }
.arm.a4 { top: 48%; transform: rotate(130deg); }
.arm.a5 { top: 63%; transform: rotate(310deg); }
.arm.a6 { top: 63%; transform: rotate(230deg); }

.pulse {
  position: absolute;
  border: 2px solid rgba(240, 207, 37, 0.76);
  border-radius: 999px;
  opacity: 0.86;
}

.pulse.p1 {
  width: 166px;
  height: 166px;
  top: 26%;
  left: calc(50% - 83px);
}

.pulse.p2 {
  width: 260px;
  height: 260px;
  top: 17%;
  left: calc(50% - 130px);
  border-color: rgba(120, 169, 65, 0.42);
}

.visual-label {
  position: absolute;
  right: 26px;
  width: 168px;
  padding: 12px 13px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.86);
  border: 1px solid rgba(41, 49, 54, 0.12);
  box-shadow: 0 10px 26px rgba(41, 49, 54, 0.09);
  font-size: 0.82rem;
  color: var(--muted);
}

.visual-label strong {
  display: block;
  color: var(--ink);
  margin-bottom: 3px;
}

.visual-label.one { top: 70px; }
.visual-label.two { top: 248px; }
.visual-label.three { bottom: 72px; }

.section {
  padding: 84px 0;
}

.section.white {
  background: var(--white);
}

.section.dark {
  color: var(--white);
  background: #20272a;
}

.section-head {
  max-width: 820px;
  margin-bottom: 36px;
}

.section-head h2 {
  margin-bottom: 14px;
  font-size: clamp(2rem, 4vw, 3.2rem);
  line-height: 1.02;
  letter-spacing: -0.03em;
}

.section-head p {
  color: var(--muted);
  font-size: 1.08rem;
}

.dark .section-head p,
.dark .muted {
  color: rgba(255, 255, 255, 0.72);
}

.focus-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 30px;
  margin: 0 0 44px;
}

.focus-block {
  padding-left: 20px;
  border-left: 3px solid var(--green);
}

.focus-block h3 {
  margin: 0 0 10px;
  font-size: 1.42rem;
  letter-spacing: -0.015em;
}

.focus-block p {
  margin: 0;
  color: var(--muted);
  font-size: 1rem;
}

.cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.card {
  padding: 28px;
  border: 1px solid rgba(41, 49, 54, 0.13);
  border-radius: 8px;
  background: var(--white);
  box-shadow: 0 14px 36px rgba(41, 49, 54, 0.07);
}

.card h3 {
  margin-bottom: 10px;
  font-size: 1.36rem;
}

.card p {
  color: var(--muted);
}

.tag {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  margin-bottom: 16px;
  padding: 0 10px;
  border-radius: 999px;
  background: var(--soft-green);
  color: var(--green-dark);
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.steps {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1px;
  overflow: hidden;
  border: 1px solid rgba(41, 49, 54, 0.12);
  border-radius: 8px;
  background: rgba(41, 49, 54, 0.12);
}

.step {
  min-height: 245px;
  padding: 28px;
  background: var(--white);
}

.step-number {
  display: inline-grid;
  place-items: center;
  width: 36px;
  height: 36px;
  margin-bottom: 34px;
  border-radius: 50%;
  background: #20272a;
  color: var(--white);
  font-weight: 800;
}

.split {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 52px;
  align-items: center;
}

.feature-list {
  display: grid;
  gap: 14px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.feature-list li {
  display: grid;
  grid-template-columns: 28px 1fr;
  gap: 12px;
  align-items: start;
  padding: 16px 0;
  border-bottom: 1px solid rgba(41, 49, 54, 0.12);
}

.check {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--yellow);
  color: #11150f;
  font-weight: 900;
}

.ip-panel {
  padding: 34px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  background:
    linear-gradient(135deg, rgba(120, 169, 65, 0.18), rgba(240, 207, 37, 0.09)),
    rgba(255, 255, 255, 0.05);
}

.ip-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 24px;
}

.ip-stat {
  padding: 18px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
}

.ip-stat strong {
  display: block;
  margin-bottom: 5px;
  font-size: 1.4rem;
}

.audiences {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.audience {
  min-height: 180px;
  padding: 24px;
  border: 1px solid rgba(41, 49, 54, 0.12);
  border-radius: 8px;
  background: linear-gradient(180deg, #ffffff, #f6f8f2);
}

.audience h3 {
  margin-bottom: 12px;
  font-size: 1.12rem;
}

.audience p {
  color: var(--muted);
  font-size: 0.95rem;
}

.quote-band {
  padding: 58px;
  border-radius: 8px;
  background:
    linear-gradient(135deg, rgba(240, 207, 37, 0.20), rgba(120, 169, 65, 0.16)),
    var(--white);
  border: 1px solid rgba(41, 49, 54, 0.12);
}

.quote-band p {
  max-width: 900px;
  margin-bottom: 18px;
  font-size: clamp(1.6rem, 3.2vw, 2.7rem);
  line-height: 1.08;
  letter-spacing: -0.025em;
}

.cta {
  padding: 74px 0;
  color: var(--white);
  background:
    radial-gradient(circle at 12% 24%, rgba(240, 207, 37, 0.22), transparent 32%),
    linear-gradient(135deg, #20272a, #111517);
}

.cta-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 32px;
  align-items: center;
}

.cta h2 {
  max-width: 740px;
  margin-bottom: 12px;
  font-size: clamp(2.1rem, 5vw, 4rem);
  line-height: 1.02;
  letter-spacing: -0.035em;
}

.cta p {
  max-width: 720px;
  margin-bottom: 0;
  color: rgba(255, 255, 255, 0.72);
  font-size: 1.08rem;
}

.site-footer {
  padding: 42px 0;
  background: #121617;
  color: rgba(255, 255, 255, 0.78);
}

.footer-grid {
  width: min(1180px, calc(100% - 36px));
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  align-items: center;
}

.footer-logo {
  width: 160px;
  height: auto;
  filter: brightness(1.05);
  background: rgba(255, 255, 255, 0.92);
  border-radius: 6px;
  padding: 6px;
}

.footer-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 18px;
  font-size: 0.92rem;
}

.footer-legal {
  width: min(1180px, calc(100% - 36px));
  margin: 32px auto 0;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}

.footer-legal p + p {
  margin-top: 6px;
}

.footer-legal p {
  margin: 0;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.82rem;
}

.note {
  margin-top: 18px;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.82rem;
}

@media (max-width: 980px) {
  .nav {
    min-height: auto;
    padding: 14px 0;
    align-items: flex-start;
  }

  .nav-links {
    display: none;
  }

  .hero {
    min-height: auto;
    padding-top: 56px;
  }

  .hero-grid,
  .split,
  .cta-row {
    grid-template-columns: 1fr;
  }

  .hero-visual {
    min-height: 520px;
  }

  .proof-strip,
  .cards,
  .steps,
  .audiences {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .hero-inner,
  .section-inner,
  .nav,
  .footer-grid {
    width: min(100% - 28px, 1180px);
  }

  h1 {
    font-size: 3.15rem;
  }

  .hero-visual {
    min-height: 470px;
  }

  .well-diagram {
    inset: 46px 18px 42px 70px;
  }

  .visual-label {
    right: 12px;
    width: 136px;
  }

  .proof-strip,
  .cards,
  .steps,
  .audiences,
  .ip-grid,
  .focus-grid,
  .footer-grid {
    grid-template-columns: 1fr;
  }

  .proof {
    padding: 16px 18px;
    border-right: 0;
    border-bottom: 1px solid rgba(41, 49, 54, 0.12);
  }

  .proof:last-child {
    border-bottom: 0;
  }

  .quote-band {
    padding: 30px;
  }

  .footer-links {
    justify-content: flex-start;
  }
}
</style>
